import { useTranslation } from "@opendash/i18n";
import { Flex, InputNumber, Select } from "antd";
import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useState } from "react";

type Props = {
  value?: NumberRangeValue;
  onChange?: (value: NumberRangeValue) => void;
};

export type NumberRangeValue = {
  start: number;
  end?: number;
  rangeEnd?: number[];
  operator?: string;
};

export const NumberRange = observer((props: Props) => {
  const t = useTranslation();
  const [value, setValue] = useState<NumberRangeValue>({
    start: undefined,
    end: undefined,
    rangeEnd: undefined,
    operator: "-",
  });

  useEffect(() => {
    props.onChange?.(value);
  }, [value]);

  function setStart(value: number) {
    setValue((prev) => ({ ...prev, start: value }));
  }

  function setEnd(value: number) {
    setValue((prev) => ({ ...prev, end: value }));
  }

  function setRangeEnd(value: number[]) {
    setValue((prev) => ({ ...prev, rangeEnd: value }));
  }

  function setSelectedOperator(value: string) {
    setValue((prev) => ({ ...prev, operator: value }));
  }

  const options = useMemo(
    () => [
      { label: t("opendash:cron.later.numberRange.operator.to"), value: "-" },
      { label: t("opendash:cron.later.numberRange.operator.and"), value: "," },
    ],
    []
  );

  return (
    <Flex gap="small" align="center">
      <InputNumber
        onChange={(value) => setStart(Number(value))}
        value={props.value?.start ?? value.start}
        placeholder={t("opendash:cron.later.numberRange.start.placeholder")}
      />
      <Select
        options={options}
        onSelect={setSelectedOperator}
        value={props.value?.operator ?? value.operator}
        placeholder={t("opendash:cron.later.numberRange.operator.placeholder")}
      />
      {value.operator === "-" && (
        <InputNumber
          onChange={(value) => setEnd(Number(value))}
          value={props.value?.end ?? value.end}
          min={(value.start ?? 0) + 1}
          placeholder={t("opendash:cron.later.numberRange.end.placeholder")}
        />
      )}
      {value.operator === "," && (
        <Select
          mode="tags"
          onChange={(values) => setRangeEnd(values.map(Number))}
          value={props.value?.rangeEnd ?? value.rangeEnd}
          placeholder={t(
            "opendash:cron.later.numberRange.rangeEnd.placeholder"
          )}
        />
      )}
    </Flex>
  );
});
